import { Colors } from '@oy/uicore';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Text } from '../../Contents';
import { Padder, Spacer } from '../../Layout';
import { Button } from '../Button';
import styles from './Highlight.module.css';
import {
  ActionButton,
  ElementPosition,
  TooltipPosition,
  TranslatePercentage,
} from './Types';
import { getPositionAndTranslatePercentage } from './Utils';

export enum HighlighType {
  Environment = 'ENVIRONMENT_DESCRIPTION',
  Onboarding = 'ONBOARDING_TASK',
  AccountUpgradeStatus = 'UPGRADE_STATUS',
  EwalletActivationMultiple = 'EWALLET_ACTIVATION_MULTIPLE',
}

export type HighlightProps = {
  active: boolean;
  title: string | React.ReactNode;
  desc?: string | React.ReactNode;
  target: string;
  leftCustomElement?: React.ReactNode;
  rightCustomElement?: React.ReactNode;
  bottomCustomElement?: React.ReactNode;
  primaryButton?: ActionButton;
  secondaryButton?: ActionButton;
  position: TooltipPosition;
  disable?: boolean;
  customHeight?: number;
  customWidth?: number;
  heightOffset?: number;
  widthOffset?: number;
  verticalOffset?: number;
  horizontalOffset?: number;
  type?: 'dark' | 'light';
  showArrow?: boolean;
  scrollable?: boolean;
};

const Highlight = ({
  active,
  title,
  desc,
  target,
  leftCustomElement,
  rightCustomElement,
  bottomCustomElement,
  primaryButton,
  secondaryButton,
  position,
  disable,
  customHeight = 0,
  customWidth = 0,
  verticalOffset = 0,
  horizontalOffset = 0,
  heightOffset = 0,
  widthOffset = 0,
  type = 'light',
  showArrow = true,
  scrollable = false,
}: HighlightProps) => {
  const [elPos, setElPos] = useState<ElementPosition>({
    top: '0px',
    left: '0px',
  });
  const [targetElement, setTargetElement] = useState({
    width: 0,
    height: 0,
    top: 0,
    left: 0,
  });
  const [percentageTranslate, setPercentageTranslate] =
    useState<TranslatePercentage>({
      x: 0,
      y: 0,
    });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const elementTarget = document.getElementById(target);
    if (!elementTarget) return;

    let timeout: NodeJS.Timeout;

    const getScrollableParent = (element: HTMLElement): HTMLElement | null => {
      let parent = element.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
        if (isScrollable && parent.scrollHeight > parent.clientHeight) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    };

    const updatePosition = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        const domPos = elementTarget.getBoundingClientRect();
        const positionGetter = (position: number) => {
          if (type === 'dark') return position - 8;
          return position;
        };

        const sizeGetter = (
          size: number,
          sizeType: 'width' | 'height',
          position: TooltipPosition,
        ) => {
          if (
            type === 'dark' &&
            ((sizeType === 'height' &&
              (position === 'top' || position === 'bottom')) ||
              (sizeType === 'width' &&
                (position === 'right' || position === 'left')))
          ) {
            return size + 16;
          }
          return size;
        };

        if (domPos) {
          let { top, height, width, left } = domPos;

          if (customHeight > 0) height = customHeight;
          if (customWidth > 0) width = customWidth;

          top = top + verticalOffset;
          left = left + horizontalOffset;
          height = height + heightOffset;
          width = width + widthOffset;

          setTargetElement({ width, height, top, left });
          const { elementPosition, percentageTranslate } =
            getPositionAndTranslatePercentage(
              positionGetter(top + 10),
              positionGetter(left + (position === 'left' ? -10 : 10)),
              sizeGetter(height, 'height', position),
              sizeGetter(width, 'width', position),
              position || 'bottom',
            );
          setElPos(elementPosition);
          setPercentageTranslate(percentageTranslate);
        }
      }, 50);
    };

    updatePosition();
    setTimeout(() => setVisible(true), 100);

    const resizeObserver = new ResizeObserver(() => updatePosition());
    resizeObserver.observe(elementTarget);
    resizeObserver.observe(document.body);

    const mutationObserver = new MutationObserver(() => updatePosition());
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    const scrollableParent = getScrollableParent(elementTarget);
    const scrollTarget = scrollableParent || window;
    scrollTarget.addEventListener('scroll', updatePosition, { passive: true });

    return () => {
      clearTimeout(timeout);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      scrollTarget.removeEventListener('scroll', updatePosition);
    };
  }, [
    active,
    position,
    target,
    type,
    customHeight,
    customWidth,
    verticalOffset,
    horizontalOffset,
    heightOffset,
    widthOffset,
  ]);

  const renderButton = (
    element: ActionButton,
    buttonType: 'primary' | 'secondary',
  ): React.ReactNode => {
    const { action, text } = element;
    return buttonType === 'primary' ? (
      <Button onClick={action}>{text}</Button>
    ) : (
      <button
        disabled={disable}
        className={classNames(styles.actionCancel, {
          [styles.light]: type === 'dark',
        })}
        onClick={action}
      >
        {text}
      </button>
    );
  };

  const TextColor = type === 'dark' ? Colors.BLACK200 : Colors.WHITE;

  if (!active) return null;
  return (
    <>
      <div
        className={classNames(styles.container, {
          [styles.visible]: visible,
          [styles.light]: type === 'dark',
        })}
        style={{
          willChange: 'transform',
          top: elPos.top,
          left: elPos.left,
          transform: `translate(${percentageTranslate.x}%,${percentageTranslate.y}%)`,
        }}
      >
        <Padder all={Padder.Size.M}>
          <Text textStyle={Text.Style.BodyXlSemibold} color={TextColor}>
            {title}
          </Text>
          <Padder top={Padder.Size.XXS} />
          <Text textStyle={Text.Style.BodyMdRegular} color={TextColor}>
            {desc}
          </Text>
          <Spacer vertical={Spacer.Size.M} />
          <div
            className={classNames(styles.actionContainer, {
              [styles.justifyRight]: !secondaryButton,
            })}
          >
            {leftCustomElement && (
              <div className={styles.leftModifyContainer}>
                {leftCustomElement}
              </div>
            )}
            {secondaryButton && renderButton(secondaryButton, 'secondary')}
            {rightCustomElement && (
              <div className={styles.rightModifyContainer}>
                {rightCustomElement}
              </div>
            )}
            {primaryButton && renderButton(primaryButton, 'primary')}
            {bottomCustomElement}
          </div>
        </Padder>
        {showArrow && (
          <span
            className={classNames(styles.arrow, {
              [styles.light]: type === 'dark',
              [styles[`arrow-${position}`]]: true,
            })}
          />
        )}
      </div>
      <div
        className={styles.backdrop}
        style={{ pointerEvents: scrollable ? 'none' : 'auto' }}
      />
      {type === 'dark' && (
        <div
          className={styles.target}
          style={{
            width: targetElement.width + 16,
            height: targetElement.height + 16,
            top: targetElement.top - 8,
            left: targetElement.left - 8,
          }}
        />
      )}
    </>
  );
};

export default Object.assign(Highlight, {
  Type: HighlighType,
});
